import React, { useEffect, useMemo } from "react"
import { Flex, Form, InputNumber, Segmented, Select, Space, Tooltip } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { Card } from "ui/Card"
import { Text } from "ui/Typography"
import styles from "./DurationAndCutOff.module.css"
import { spacings } from "themes/variables"
import { useWizardContext } from "../../../WizardContext"
import { CUT_OFF_UNIT_OPTIONS, DEFAULT_DURATION_UNIT, DURATION_UNIT_OPTIONS } from "../../../constants/schedulingConstants"

export const DurationAndCutOff = () => {
  const { t } = useTranslation()
  const { form } = useWizardContext()
  const durationType = Form.useWatch("duration_type", form) ?? form.getFieldValue("duration_type")
  const numberOfDays = Form.useWatch("number_of_days", form) ?? form.getFieldValue("number_of_days")
  const durationUnit = Form.useWatch("duration_unit", form) ?? form.getFieldValue("duration_unit")
  const isMultiDay = durationType === "multi_day"

  // The unit picker shows its first option as chosen even when the form holds none (a new listing,
  // or one just switched from multi-day), so the form would ask for a unit that looks picked. Give it
  // that unit. Set in code, so the form isn't marked as changed.
  useEffect(() => {
    if (!isMultiDay && !durationUnit) form.setFieldValue("duration_unit", DEFAULT_DURATION_UNIT)
  }, [form, isMultiDay, durationUnit])

  const durationUnitOptions = useMemo(() => DURATION_UNIT_OPTIONS.map(({ value, labelKey }) => ({ value, label: t(labelKey) })), [t])
  const cutOffUnitOptions = useMemo(() => CUT_OFF_UNIT_OPTIONS.map(({ value, labelKey }) => ({ value, label: t(labelKey) })), [t])

  return (
    <Card title={t("experiences.ticketed_schedule.duration_card_title")} className="mb-xl">
      <Flex gap={48} wrap="wrap">
        <Form.Item
          label={<Text className={styles.sectionLabel}>{t("experiences.ticketed_schedule.experience_duration")}</Text>}
          required={!isMultiDay}
          style={{ marginBottom: 0 }}
        >
          {isMultiDay ? (
            <Flex align="center" gap={spacings.XS} className={styles.durationPill}>
              <InfoCircleOutlined className={styles.durationPillIcon} />
              <Text className={styles.durationPillText}>{t("experiences.ticketed_schedule.multi_day_duration", { count: numberOfDays ?? "—" })}</Text>
            </Flex>
          ) : (
            <Flex align="center" gap={spacings.XS}>
              <Form.Item name="duration_value" noStyle rules={[{ required: true, message: t("experiences.ticketed_schedule.duration_value_required") }]}>
                <InputNumber min={1} className={styles.numberInput} size="large" />
              </Form.Item>
              <Form.Item name="duration_unit" noStyle rules={[{ required: true, message: t("experiences.ticketed_schedule.duration_unit_required") }]}>
                <Segmented options={durationUnitOptions} size="large" className={styles.segmented} />
              </Form.Item>
            </Flex>
          )}
        </Form.Item>

        <Form.Item
          label={
            <Flex align="center" gap={spacings.XS}>
              <Text className={styles.sectionLabel}>{t("experiences.ticketed_schedule.cut_off_label")}</Text>
              <Tooltip placement="right" title={t("experiences.ticketed_schedule.cut_off_tooltip")}>
                <InfoCircleOutlined className={styles.infoIcon} />
              </Tooltip>
            </Flex>
          }
          required
          className="mb-0"
        >
          <Space.Compact size="large">
            <Form.Item name="cut_off_value" noStyle rules={[{ required: true, message: t("experiences.ticketed_schedule.cut_off_value_required") }]}>
              <InputNumber min={0} className={styles.numberInput} />
            </Form.Item>
            <Form.Item name="cut_off_unit" noStyle rules={[{ required: true, message: t("experiences.ticketed_schedule.cut_off_unit_required") }]}>
              <Select options={cutOffUnitOptions} className={styles.unitSelect} />
            </Form.Item>
          </Space.Compact>
        </Form.Item>
      </Flex>
    </Card>
  )
}
